import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { OutsideLink } from '@openx/components-core/lib/Link/Link.tsx';
import { Controller, useFormContext } from 'react-hook-form';

import { getEnvironmentConfigs } from '../../../environmentConfigs.ts';

export interface OxsTermsFieldProps {
  /** locks the box checked, so a user who already accepted can't untick it and lose Authorize */
  isAccepted: boolean;
  isError: boolean;
  isRetrying: boolean;
  onRetry: () => void;
}

/**
 * The OXS T&C checkbox bound to the `termsAccepted` form field, or an error with a retry when the lookup failed.
 */
export function OxsTermsField({ isAccepted, isError, isRetrying, onRetry }: OxsTermsFieldProps) {
  const { oxs } = getEnvironmentConfigs();
  const { control } = useFormContext<{ termsAccepted: boolean }>();

  if (isError) {
    return (
      <Alert
        severity="error"
        data-test="oxs-terms-error"
        action={
          <Button color="inherit" size="small" onClick={onRetry} disabled={isRetrying}>
            Retry
          </Button>
        }
      >
        Could not load the OpenXSelect Terms and Conditions.
      </Alert>
    );
  }

  return (
    <FormControlLabel
      data-test="oxs-terms"
      label={
        <>
          I agree to the OpenXSelect{' '}
          <OutsideLink to={oxs.termsUrl} openInNewTab data-test="oxs-terms-link">
            Terms and Conditions
          </OutsideLink>
        </>
      }
      control={
        <Controller
          name="termsAccepted"
          control={control}
          render={({ field }) => (
            <Checkbox checked={field.value} onChange={field.onChange} onBlur={field.onBlur} disabled={isAccepted} />
          )}
        />
      }
    />
  );
}
